/**
 * F2 — Text Overlay (`client_text`).
 *
 * Renders a single-line text layer over the source image with controls for
 * font family / size / color / bold / italic / underline / alignment + a
 * draggable position indicator. Composite is a flat PNG via canvas 2D.
 *
 * Pure non-AI typography. Stylized typography uses C5 (Ideogram).
 */

import { registerClientHandler } from './registry.js'
import { loadImage } from '../utils/mockup-composite.js'

const DEFAULT_FONTS = [
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Bricolage Grotesque',
  'Rubik',
  'Comic Sans MS',
  'Playfair Display',
]

let _activeListeners = []
let _activeRoot = null

function track(target, event, fn) {
  target.addEventListener(event, fn)
  _activeListeners.push(() => target.removeEventListener(event, fn))
}

function cleanup() {
  while (_activeListeners.length) {
    try { _activeListeners.pop()() } catch {}
  }
  _activeRoot = null
}

// ---- Public handler ----

export const clientTextHandler = {
  mountControls(rootEl, ctx) {
    cleanup()
    _activeRoot = rootEl
    return mountTextUI(rootEl, ctx)
  },

  unmount() {
    cleanup()
    if (_activeRoot) _activeRoot.innerHTML = ''
  },

  async run(ctx) {
    const layer = makeDefaultLayer()
    const dataUrl = await composeTextLayer(ctx.image, layer)
    return { result_data_url: dataUrl, metadata: layer }
  },
}

registerClientHandler('clientTextHandler', clientTextHandler)

// ---- Implementation ----

// Image-content color, not themed UI chrome — the default text color is baked
// into the exported image pixels and must not follow the theme (white is the
// standard default over arbitrary artwork).
function makeDefaultLayer() {
  return {
    text: 'Your text here',
    x: 0.5,
    y: 0.5,
    fontFamily: DEFAULT_FONTS[0],
    fontSize: 96,
    color: '#ffffff',
    bold: false,
    italic: false,
    underline: false,
    align: 'center',
  }
}

function mountTextUI(rootEl, ctx) {
  const fonts = ctx.config?.fonts_default || DEFAULT_FONTS
  const layer = makeDefaultLayer()

  rootEl.innerHTML = `
    <div class="ctt-text">
      <div class="ctt-controls">
        <input type="text" id="ctTxt" value="${escapeAttr(layer.text)}" placeholder="Your text here" maxlength="200" />
      </div>
      <div class="ctt-controls">
        <label>Font</label>
        <select id="ctFont">
          ${fonts.map(f => `<option value="${escapeAttr(f)}" ${f === layer.fontFamily ? 'selected' : ''}>${escapeAttr(f)}</option>`).join('')}
        </select>
        <label>Size</label>
        <input type="range" id="ctSize" min="12" max="240" value="${layer.fontSize}" />
        <span class="ctt-value" data-bind="size">${layer.fontSize}px</span>
        <label>Color</label>
        <input type="color" id="ctColor" value="${layer.color}" />
      </div>
      <div class="ctt-controls">
        <button type="button" class="ctt-pill" data-toggle="bold"><b>B</b></button>
        <button type="button" class="ctt-pill" data-toggle="italic"><i>I</i></button>
        <button type="button" class="ctt-pill" data-toggle="underline"><u>U</u></button>
        <span class="ctt-spacer"></span>
        <label>Align</label>
        <button type="button" class="ctt-pill active" data-align="left">L</button>
        <button type="button" class="ctt-pill active" data-align="center">C</button>
        <button type="button" class="ctt-pill" data-align="right">R</button>
      </div>
      <p class="ctt-hint">Tip: drag the text on the result image to reposition.</p>
    </div>
  `

  bindBasic(rootEl, layer, ctx)
}

function bindBasic(rootEl, layer, ctx) {
  const txtEl = rootEl.querySelector('#ctTxt')
  const fontEl = rootEl.querySelector('#ctFont')
  const sizeEl = rootEl.querySelector('#ctSize')
  const sizeLabel = rootEl.querySelector('[data-bind="size"]')
  const colorEl = rootEl.querySelector('#ctColor')

  track(txtEl, 'input', () => { layer.text = txtEl.value; emitDebounced() })
  track(fontEl, 'change', () => { layer.fontFamily = fontEl.value; emitDebounced() })
  track(sizeEl, 'input', () => {
    layer.fontSize = Number(sizeEl.value)
    if (sizeLabel) sizeLabel.textContent = `${layer.fontSize}px`
    emitDebounced()
  })
  track(colorEl, 'input', () => { layer.color = colorEl.value; emitDebounced() })

  rootEl.querySelectorAll('[data-toggle]').forEach(btn => {
    track(btn, 'click', () => {
      const k = btn.dataset.toggle
      layer[k] = !layer[k]
      btn.classList.toggle('active', layer[k])
      emit()
    })
  })

  rootEl.querySelectorAll('[data-align]').forEach(btn => {
    track(btn, 'click', () => {
      layer.align = btn.dataset.align
      rootEl.querySelectorAll('[data-align]').forEach(b => b.classList.toggle('active', b === btn))
      emit()
    })
  })

  let emitTimer = null
  function emitDebounced() {
    clearTimeout(emitTimer)
    emitTimer = setTimeout(emit, 80)
  }

  async function emit() {
    try {
      const dataUrl = await composeTextLayer(ctx.image, layer)
      ctx.onResult?.({
        result_data_url: dataUrl,
        metadata: { ...layer },
      })
    } catch (err) {
      ctx.onError?.(err)
    }
  }

  emit()
}

async function composeTextLayer(imageUrl, layer) {
  await ensureFontReady(layer.fontSize, layer.fontFamily)
  const img = await loadImage(imageUrl)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const c = canvas.getContext('2d')
  c.drawImage(img, 0, 0)

  const fontStyle = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${layer.fontSize}px ${layer.fontFamily}`
  c.font = fontStyle
  c.fillStyle = layer.color
  c.textAlign = layer.align
  c.textBaseline = 'middle'

  const x = layer.x * canvas.width
  const y = layer.y * canvas.height
  c.fillText(layer.text, x, y)

  if (layer.underline) {
    const metrics = c.measureText(layer.text)
    let underlineX = x
    if (layer.align === 'center') underlineX = x - metrics.width / 2
    else if (layer.align === 'right') underlineX = x - metrics.width
    c.fillRect(
      underlineX,
      y + layer.fontSize * 0.5,
      metrics.width,
      Math.max(2, layer.fontSize * 0.05),
    )
  }

  return canvas.toDataURL('image/png')
}

async function ensureFontReady(size, family) {
  if (typeof document === 'undefined' || !document.fonts?.load) return
  try {
    await document.fonts.load(`${size}px ${family}`)
  } catch {
    // best-effort; if font isn't loadable canvas falls back to system default
  }
}

function escapeAttr(s) {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
}
